import type { PaymentStage } from '@krp/sale-ui'

import { sameId } from '../../../shared/api/wire'
import type { CommandFeedback } from '../../../shared/model/feedback'
import { canEditCart } from './cartRules'
import { PREPARE_COMMANDS, SAVE_COMMAND, WAIT_RECEIPT_COMMAND, type PaymentAttempt } from './paymentAttemptsSlice'
import type { AqsiSale, AqsiSaleOperation, SaleCommand } from './types'

/** Код внимания службы: после отмены или отказа оплаты нашёлся слип покупки на ту же сумму с этой кассы. */
export const LATE_PAYMENT_SLIP = 'late-payment-slip'

/** Столько окно ждёт, пока служба сама отправит чек после доказанной оплаты, прежде чем сказать «чек не отправлен». */
export const SERVICE_RECEIPT_GRACE_MS = 10_000

/** Вход переходов окна оплаты: только то, что знает модуль, — продажа, её операция, команда в пути, попытка и её ответы. */
export interface PaymentStageInput {
  /** Незавершённая продажа кассы; `null` — продажи нет (или она уже закрыта). */
  sale: AqsiSale | null
  /** Итог показан: продажа закрыта чеком в этой вкладке. */
  finished: boolean
  /** Текущая операция продажи: из ответа продажи (`currentOperation`), иначе отдельное чтение. */
  operation: AqsiSaleOperation | undefined
  /** Опасная команда в пути или с неизвестным исходом. */
  command: SaleCommand | undefined
  /**
   * Быстрая оплата: продажа только что ушла в фон (деньги взяты, чек печатается отдельно) — окно показывает галочку «Оплата прошла» и
   * закрывается само. Не передано — быстрой оплаты нет.
   */
  handedOff?: boolean
  /** Попытка ЭТОЙ продажи; чужая или устаревшая — `null`. */
  attempt: PaymentAttempt | null
  /** Ход и отказ команд цикла этой попытки. */
  feedback: CommandFeedback | null
  now: number
}

const UNRESOLVED: ReadonlySet<string> = new Set(['Pending', 'Sent', 'Unknown'])

/** Операция ждёт ручной сверки: служба назначила, итог ещё не записан. */
export function manualPending(operation: AqsiSaleOperation | undefined): boolean {
  return operation !== undefined && UNRESOLVED.has(operation.state) && operation.nextAction === 'ManualReconciliation'
}

/** Операция списания этой продажи (а не чек и не прошлая попытка). */
export function purchaseOf(sale: AqsiSale, operation: AqsiSaleOperation | undefined): AqsiSaleOperation | undefined {
  return operation !== undefined && operation.kind === 'purchase' && sale.purchaseOperationId !== null && sameId(operation.operationId, sale.purchaseOperationId)
    ? operation
    : undefined
}

export function hasLateSlip(sale: AqsiSale): boolean {
  return sale.attention.some((item) => item.code === LATE_PAYMENT_SLIP)
}

/** Отказ этой попытки показывается окном: её видели идущей в этой вкладке, кассир её не отменял и не закрывал окно. */
export function declineShown(attempt: PaymentAttempt | null): boolean {
  return attempt !== null && attempt.live && !attempt.closed && !attempt.cancelledByCashier
}

/**
 * Этап окна оплаты — ЧИСТАЯ функция (RP-PAY-ONE): одна таблица переходов вместо веток по экранам. `null` — окна нет.
 *
 * Отказ, тихую отмену и закрытие решают нажатия кассира (`attempt`), а не порядок ответов: запоздавшее чтение продажи, итог отмены и
 * живое событие могут прийти в любом порядке — этап по ним не прыгает назад к «Не оплачено». Неизвестный исход не повторяется, а
 * уточняется (`checking`).
 */
export function paymentStageOf(input: PaymentStageInput): PaymentStage | null {
  const { sale, operation, command, attempt, feedback } = input
  if (input.finished || input.handedOff === true) return 'done'
  // «Очистить» — не оплата: её ход окна оплаты не открывает.
  if (command?.kind === 'cancel') return null
  if (attempt?.resolving === true && manualPending(operation)) return 'manual'
  if (command?.phase === 'sending') return command.kind === 'fiscalize' ? 'receipt' : command.kind === 'outside' ? 'no-receipt' : 'preparing'
  if (command?.phase === 'unknown') return 'checking'

  // До денег: подготовка (проверка связи, смена способа оплаты) или её отказ — «Оплата не началась». Новое нажатие важнее итога прошлой
  // команды («не дошла»), поэтому подготовка — раньше.
  const beforeMoney = sale === null || canEditCart(sale)
  const preparing = feedback !== null && PREPARE_COMMANDS.has(feedback.command)
  if (beforeMoney && preparing && feedback.phase === 'sending') return feedback.command === SAVE_COMMAND || feedback.command === WAIT_RECEIPT_COMMAND ? 'saving' : 'preparing'
  if (command?.phase === 'not-arrived' && command.kind === 'pay') return attempt?.closed === true ? null : 'not-started'
  if (beforeMoney && preparing && (feedback.phase === 'rejected' || feedback.phase === 'unknown')) return attempt?.closed === true ? null : 'not-started'

  if (sale === null || sale.isFinished) return null
  if (sale.payment === 'InProgress') {
    const purchase = purchaseOf(sale, operation)
    const answered = purchase?.providerStatus === 'Error' || purchase?.providerStatus === 'Canceled' || purchase?.providerStatus === 'Timeout'
    return answered || manualPending(purchase) ? 'checking' : 'terminal'
  }
  if (sale.payment === 'Unknown') return 'checking'
  if (sale.paymentKind === 'Card' && sale.payment === 'Failed' && sale.fiscal === 'Pending') {
    if (!declineShown(attempt)) return null
    return hasLateSlip(sale) ? 'late-slip' : 'declined'
  }
  const moneyIn = sale.paymentKind === 'Card' ? sale.payment === 'Succeeded' : sale.payment === 'NotRequired'
  if (!moneyIn) return null
  if (sale.fiscal === 'InProgress' || sale.fiscal === 'Unknown') return 'receipt'
  if (sale.fiscal === 'Failed') return 'no-receipt'
  if (sale.fiscal === 'Pending' && sale.paymentKind === 'Card') {
    const paidAt = Date.parse(sale.updatedAt)
    const fresh = !Number.isNaN(paidAt) && input.now - paidAt < SERVICE_RECEIPT_GRACE_MS && sale.attention.length === 0
    return fresh ? 'receipt' : 'no-receipt'
  }
  return null
}
